import { useEffect, useRef } from 'react';
import { AnalyticsBanner } from './AnalyticsBanner';
import { analyticsBackend } from '../lib/site-analytics';
import { buildAnalyticsItems, type ActivitySnapshot, type PlatformSnapshot } from '../lib/analytics-values';
import { useMarkets } from '../lib/market-context';
import { useLive } from '../lib/hooks';

export function AnalyticsStats() {
  const markets = useMarkets();
  const lastActivity = useRef<ActivitySnapshot | null>(null), lastResources = useRef<PlatformSnapshot | null>(null);
  const state = useLive(async () => {
    const actor = await analyticsBackend();
    const [activity, resources] = await Promise.allSettled([actor.analytics_snapshot(), actor.platform_metrics()]);
    if (activity.status === 'fulfilled') lastActivity.current = activity.value;
    if (resources.status === 'fulfilled') lastResources.current = resources.value;
    return { activity: lastActivity.current, resources: lastResources.current,
      activityError: activity.status === 'rejected' ? 'Usage counters await the analytics backend or its connection.' : '',
      resourcesError: resources.status === 'rejected' ? 'ICP and resource snapshots could not refresh.' : '' };
  }, 'platform-analytics', 30_000);
  useEffect(() => {
    window.addEventListener('soonscreener:analytics-updated', state.refresh);
    return () => window.removeEventListener('soonscreener:analytics-updated', state.refresh);
  }, [state.refresh]);
  const activity = state.data?.activity ?? null, resources = state.data?.resources ?? null;
  const activityError = state.error || state.data?.activityError;
  const resourcesError = state.error || state.data?.resourcesError;
  const items = buildAnalyticsItems({ activity, resources, overview: markets.data, activityError, resourcesError, marketError: markets.error });
  const notes = [activityError, resourcesError, activity?.test_mode ? 'Local development counts and test ICP balances.' : '',
    activity && (activity.rate_limited || activity.visitor_capacity_reached || activity.session_capacity_reached || activity.account_capacity_reached) ? 'Activity tracking reached a limit; affected totals may be lower bounds.' : ''].filter(Boolean).join(' ');
  return <AnalyticsBanner items={items} updatedAt={activity ? Number(activity.observed_at / 1_000_000n) : undefined} trackingSince={activity ? Number(activity.since / 1_000_000n) : undefined} error={notes} />;
}
